import { X } from "lucide-react";
import type { ComponentType } from "react";
import {
  FilterSelectionBadges,
  FILTER_BADGE_REMOVE_ICON_CLASS,
} from "../shared/filter_selection_badges";
import type { CityLike, ServiceLike } from "../shared/filter_selection_badges";
import {
  DEFAULT_ALPHABETICAL_SORT,
  DEFAULT_PUBLISHED_AT_SORT,
  type AlphabeticalSortOption,
  type JobSearchFilterBadgesLabels,
  type PublishedAtSortOption,
  type ToolbarBadgeProps,
} from "./types";

export type { ServiceLike, CityLike };

export type JobSearchFilterBadgesProps<
  TService extends ServiceLike,
  TCity extends CityLike,
> = {
  labels: JobSearchFilterBadgesLabels;
  getLanguageDisplayLabel: (code: string) => string;
  selectedLanguages: string[];
  selectedServices: TService[];
  selectedCities: TCity[];
  alphabeticalSort: AlphabeticalSortOption | null;
  publishedAtSort: PublishedAtSortOption | null;
  onRemoveLanguage: (code: string) => void | Promise<void>;
  onRemoveService: (id: number) => void | Promise<void>;
  onRemoveCity: (id: number) => void | Promise<void>;
  onRemoveAlphabeticalSortBadge: () => void | Promise<void>;
  onRemovePublishedAtSortBadge: () => void | Promise<void>;
  Badge: ComponentType<ToolbarBadgeProps>;
  badgeRowClassName?: string;
  badgeFiltersClassName?: string;
  badgeSortsClassName?: string;
};

export function JobSearchFilterBadges<
  TService extends ServiceLike,
  TCity extends CityLike,
>({
  labels,
  getLanguageDisplayLabel,
  selectedLanguages,
  selectedServices,
  selectedCities,
  alphabeticalSort,
  publishedAtSort,
  onRemoveLanguage,
  onRemoveService,
  onRemoveCity,
  onRemoveAlphabeticalSortBadge,
  onRemovePublishedAtSortBadge,
  Badge,
  badgeRowClassName = "mb-4 flex w-full flex-col gap-4 md:flex-row md:justify-between md:px-0",
  badgeFiltersClassName = "flex flex-wrap gap-2",
  badgeSortsClassName = "flex flex-wrap gap-2 md:justify-end",
}: JobSearchFilterBadgesProps<TService, TCity>) {
  const hasBadges =
    selectedLanguages.length > 0 ||
    selectedServices.length > 0 ||
    selectedCities.length > 0 ||
    (alphabeticalSort != null &&
      alphabeticalSort !== DEFAULT_ALPHABETICAL_SORT) ||
    (publishedAtSort != null && publishedAtSort !== DEFAULT_PUBLISHED_AT_SORT);

  if (!hasBadges) {
    return null;
  }

  return (
    <div className={badgeRowClassName}>
      <div className={badgeFiltersClassName}>
        <FilterSelectionBadges
          labels={labels}
          getLanguageDisplayLabel={getLanguageDisplayLabel}
          selectedLanguages={selectedLanguages}
          selectedServices={selectedServices}
          selectedCities={selectedCities}
          onRemoveLanguage={onRemoveLanguage}
          onRemoveService={onRemoveService}
          onRemoveCity={onRemoveCity}
          Badge={Badge}
        />
      </div>

      <div className={badgeSortsClassName}>
        {alphabeticalSort != null &&
          alphabeticalSort !== DEFAULT_ALPHABETICAL_SORT && (
            <Badge
              variant="light"
              color="light"
              size="md"
              endIcon={
                <X
                  className={FILTER_BADGE_REMOVE_ICON_CLASS}
                  onClick={() => {
                    void onRemoveAlphabeticalSortBadge();
                  }}
                />
              }
            >
              {labels.badgeAlphabeticalPrefix}:{" "}
              {labels.sortAlphabeticalLabel(alphabeticalSort)}
            </Badge>
          )}
        {publishedAtSort != null &&
          publishedAtSort !== DEFAULT_PUBLISHED_AT_SORT && (
            <Badge
              variant="light"
              color="light"
              size="md"
              endIcon={
                <X
                  className={FILTER_BADGE_REMOVE_ICON_CLASS}
                  onClick={() => {
                    void onRemovePublishedAtSortBadge();
                  }}
                />
              }
            >
              {labels.badgePublishedAtPrefix}:{" "}
              {labels.sortPublishedAtLabel(publishedAtSort)}
            </Badge>
          )}
      </div>
    </div>
  );
}
